/* eslint-disable max-lines */

import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { NavLink } from 'react-router-dom'

import FavoriteIcon from 'pages/DeviceSearch/FavoriteIcon'
import PremiumLabel from 'pages/shared/PremiumLabel'

import Tooltip from 'components/Tooltip'
import Checkbox from 'components/Checkbox'

import Api from 'services/api'

import { useSessionContext } from 'hooks/SessionContext'

import { color } from 'styles/colors'
import { screenSize } from 'styles/shared'

type Note = { content: string } | null

type ListItemProps = {
  pathRoot: string
  note?: Note
  deviceId: string
  selectedDeviceIds: string[]
  dealerName?: string
  dealerPhoneNumber?: string
  dealerTier?: string
  serialNumber?: string
  updateSelectedDevicesIdsCallback: (ids: string[]) => void
  getDeviceLinkCallback: (id: string) => string
  isPremiumDealerExists?: boolean
}

const ListItem = ({ pathRoot, note = null, deviceId, selectedDeviceIds, dealerName = '', dealerPhoneNumber = '', dealerTier = '', serialNumber = '', updateSelectedDevicesIdsCallback, getDeviceLinkCallback, isPremiumDealerExists = false }: ListItemProps) => {
  const { session: { isAdmin, isFsr, isTechnician, dealerId } } = useSessionContext()
  const storageId = pathRoot + 'Selected'
  const [deviceSelected, setDeviceSelected] = useState(false)

  const [value, setValue] = useState(note ? note.content : '')
  const [oldContent, setOldContent] = useState(note ? note.content : '')
  const [error, setError] = useState(false)
  const [saved, setSaved] = useState(false)

  const isUserValidForFavorites = isFsr || isAdmin || isTechnician
  let timeOut

  useEffect(() => {
    setDeviceSelected(selectedDeviceIds.includes(deviceId))
  }, [selectedDeviceIds, deviceId])

  const onSave = () => {
    if ((value && value !== oldContent)) {
      setSaved(false)

      if (timeOut) {
        clearTimeout(timeOut)
      }

      Api.Notes.create(dealerId, deviceId, value)
        .then(() => {
          setOldContent(value)
          setError(false)
          setSaved(true)
        })
        .catch(() => {
          setError(true)
          setSaved(false)
        }).finally(() => {
          timeOut = setTimeout(() => {
            setSaved(false)
            setError(false)
          }, 3000)
        })
    }
  }

  const onChange = () => {
    if (deviceSelected) {
      updateSelectedDevicesIdsCallback(selectedDeviceIds.filter(v => v !== deviceId))
    } else {
      updateSelectedDevicesIdsCallback([...selectedDeviceIds, deviceId])
    }

    setDeviceSelected(!deviceSelected)
  }

  return (
    <S.Container {...{ isUserValidForFavorites }}>
      {isUserValidForFavorites && <S.FavoriteIcon {...{ deviceId }} />}
      <S.Checkbox label="" checked={deviceSelected} {...{ onChange }} />

      <S.LeftSide isPremiumDealer={isPremiumDealerExists}>
        <S.Auid>
          AUID: {
            <NavLink
            to={`/${pathRoot}/multiple/${getDeviceLinkCallback(deviceId)}`}
            state={{ selectedDevicesStorageId: storageId }}
            onClick={() => { localStorage.setItem(storageId, deviceId) }}
            >
              {deviceId}
            </NavLink>}
        </S.Auid>

        <S.SerialNumber>S/N: {serialNumber || '--'}</S.SerialNumber>

        <S.DealerInfo>
          Dealer: {dealerName ? `${dealerName}, ${dealerPhoneNumber}` : '--'}
        </S.DealerInfo>

        {dealerTier === 'premium' ? <S.Premium /> : null}
      </S.LeftSide>

      {!isTechnician && (
        <S.RightSide {...{ isUserValidForFavorites }}>
          <S.NoteInput
            type="text"
            value={value}
            placeholder="Notes"
            onChange={(e) => setValue(e.target.value)}
            onBlur={onSave}
            onKeyPress={(e) => { if (e.key === 'Enter') onSave() }}
          />
          <S.AlertContainer>
            {saved && <S.SavedAlert saved={saved}><i className="icon-ok" />Saved</S.SavedAlert>}

            {error && (
              <Tooltip title="The note failed to save">
                <S.ErrorAlert><i className="icon-remove" />  Error</S.ErrorAlert>
              </Tooltip>
            )}
          </S.AlertContainer>
        </S.RightSide>
      )}
    </S.Container>
  )
}

export default ListItem

const S = {
  Container: styled.div.withConfig({
    shouldForwardProp: (prop) => !['isUserValidForFavorites'].includes(prop)
  })<{ isUserValidForFavorites?: boolean }>`
    display: grid;
    grid-template-columns: ${({ isUserValidForFavorites }) => isUserValidForFavorites ? 'auto' : ''} auto minmax(0, 4fr) minmax(0, 2fr) minmax(0, 1fr);
    gap: 10px;
    margin-bottom: 5px;

    @media ${screenSize.medium} {
      grid-template-columns:  ${({ isUserValidForFavorites }) => isUserValidForFavorites ? 'auto' : ''} auto minmax(0, 1fr) minmax(0, 1.6fr);
      margin-bottom: 10px;
      align-items: start;
    }

    @media ${screenSize.small} {
      grid-template-columns:  ${({ isUserValidForFavorites }) => isUserValidForFavorites ? 'auto' : ''} auto minmax(0, 1fr);
      margin-bottom: 10px;
    }
  `,
  LeftSide: styled.div.withConfig({
    shouldForwardProp: (prop) => !['isPremiumDealer'].includes(prop)
  })<{ isPremiumDealer?: boolean }>`
    display: grid;
    align-self: start;
    grid-template-columns: minmax(170px, 1fr) minmax(155px, 1fr) minmax(0, 2fr) ${({ isPremiumDealer }) => isPremiumDealer ? ' minmax(100px, 1fr)' : ''};
    grid-template-areas: "auid serial dealer premium";
    gap: 10px;

    @media ${screenSize.medium} {
      grid-template-columns: 1fr;
      grid-template-areas:
        "auid"
        "serial"
        "dealer"
        "premium";
    }

    @media ${screenSize.small} {
      grid-template-columns: 1fr;
      grid-template-areas:
        "auid"
        "serial"
        "dealer"
        "premium";
    }
  `,
  RightSide: styled.div.withConfig({
    shouldForwardProp: (prop) => !['isUserValidForFavorites'].includes(prop)
  })<{ isUserValidForFavorites?: boolean }>`
    display: flex;

    @media ${screenSize.medium} {
      grid-column: ${({ isUserValidForFavorites }) => isUserValidForFavorites ? '4' : '3'};
      grid-row: 1;
    }

    @media ${screenSize.small} {
      grid-column: ${({ isUserValidForFavorites }) => isUserValidForFavorites ? '3' : '2'};
      grid-row: 2;
    }
  `,
  FavoriteIcon: styled(FavoriteIcon)`
    grid-column: 1;
    align-self: start;
  `,
  Checkbox: styled(Checkbox)`
    display: inline-block;
    align-self: start;
  `,
  Auid: styled.div`
    grid-area: auid;
    min-width: 170px;
  `,
  SerialNumber: styled.div`
    grid-area: serial;
    min-width: 155px;
  `,
  DealerInfo: styled.div`
    grid-area: dealer;
    width: 100%;
    min-width: 0;
    padding-right: 10px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  `,
  Premium: styled(PremiumLabel)`
    grid-area: premium;
    white-space: nowrap;
    justify-self: center;
    margin: 0 6px;
    width: 100px;

    @media ${screenSize.medium} {      
      justify-self: start;
      margin: 0;
    }

    @media ${screenSize.small} {
      justify-self: start;
    }
  `,
  NoteInput: styled.input`
    flex: 1;
    min-width: 0;
    height: 28px;
    border-radius: 5px;
    background-color: ${color('buttonBackground')};
    width: 100%;

    &::placeholder {      
      opacity: 0.5;
    }

    @media ${screenSize.medium} {
      border-radius: 10px;
      height: 45px;
    }
  `,
  AlertContainer: styled.div`
    cursor: default;
  `,
  SavedAlert: styled.div.withConfig({
    shouldForwardProp: (prop) => !['saved'].includes(prop)
  })<{ saved?: boolean }>`
    color: ${color('successText')};

    ${({ saved }) => saved ? '' : `
      opacity: 0;
      transition: all 250ms linear 2s;
    `}
  `,
  ErrorAlert: styled.div`
    color: ${color('errorText')};
  `
}
